import type { AnalyStereo, AnalySubmitBody } from './analyContract';

/**
 * analySession — 진단 한 회차의 서버 제출을 맡는다.
 *
 * **제출 실패가 진단을 막아서는 안 된다.** 소비자 온보딩이라 사용자는 목소리를 내러 왔지
 * 네트워크를 고치러 온 게 아니다. 그래서 이 모듈은 무엇도 던지지 않고, 실패한 제출을
 * 큐에 남겨 다음 기회에 다시 보낸다. 호출부(`useRecordMachine`)는 결과를 안 본다.
 *
 * 재시도 형태는 [[dou-app-network-resilience-reconnect]]에서 물려받았다 — 3회 + 지수
 * 백오프 + 큐 flush. **원인 조건은 다르다**: 그쪽은 WebSocket 재연결 시 메시지 유실이라
 * 재연결 이벤트가 트리거였고, 여기는 HTTP 단발이라 트리거가 **다음 제출**뿐이다. 그래서
 * `online`·`visibilitychange` 리스너를 달지 않는다 — 달면 물려받지 않아야 할 부분까지
 * 가져온 것이다.
 *
 * 큐가 PUT만 담는 것도 계약이다. `POST /analy/0`은 `analyNo` 원자 증가라 비멱등이고
 * (SPEC D28) 멱등키가 없어서, 재시도하면 진단이 하나 더 생긴다. 그 루트 확보는
 * `analyId`(주입)가 맡고 여기서는 재시도하지 않는다. 테이크 제출은 반대로
 * `<parentId>:<stereo>` 복합키를 덮어써서 멱등이다(D18·C5) — 그래서 몇 번을 다시 보내도
 * 안전하고, 같은 테이크를 다시 내면 마지막 것만 남는다.
 */

/** 전송 결과. `status`가 없으면 응답 자체를 못 받은 것(네트워크 실패)이다. */
export type TransportResult = { ok: true } | { ok: false; status?: number };

export interface AnalyTransport {
  submit(analyId: string, body: AnalySubmitBody): Promise<TransportResult>;
}

export interface AnalySessionDeps {
  /** `null`이면 서버가 꺼져 있다 — 아무 요청도 만들지 않는다. */
  transport: AnalyTransport | null;
  /**
   * 이 회차의 서버 루트 id. 없으면 만든다(`analyTransport`의 단일 비행 확보).
   * `null`이면 이번 회차의 서버 기록을 포기하고 로컬로만 진행한다.
   */
  analyId: () => Promise<string | null>;
  /**
   * 저장된 루트 id를 버린다. `stale`(404) 한 번에 대해서만 불린다 — 다음 `analyId()`가
   * 새 루트를 만들게 하는 것이 목적이다. 없으면 404는 `drop`으로 남는다.
   */
  resetAnalyId?: () => void;
  /** 테스트가 실제로 기다리지 않게 주입한다. */
  wait?: (ms: number) => Promise<void>;
}

export interface FinalResult {
  analyId: string | null;
  stale: boolean;
}

export interface AnalySession {
  /** 테이크를 제출한다. 실패해도 던지지 않는다. */
  submitTake(body: AnalySubmitBody): Promise<void>;
  /**
   * 회차를 마감한다 — 큐를 최종본으로 갈아끼우고 **받은 순서대로** 보낸다.
   *
   * 갈아끼우는 이유: 테이크 경계에서 보낸 값은 부분적이다(저음 테이크 직후엔 음역이 없다).
   * 최종 리포트가 선 지금이 같은 `stereo`를 덮어쓸 자리고, 서버는 복합키라 덮어쓰기가
   * 안전하다(D18·C5). 남아 있던 재시도 대상도 최종본으로 대체된다.
   *
   * 순서가 계약이다: 회차 산출물을 실은 제출이 **마지막**에 나가야 서버의 `done` 전이와
   * 집계(D20)가 그 제출에서 발화한다.
   *
   * **이 마감이 실제로 보낸 루트**를 돌려준다 — 마감 전에 읽은 저장소 id와 다를 수 있다
   * (회차 중 루트를 못 만들어 마감에서 처음 만들었거나, 404로 새 루트에서 마감했다).
   * `analyId`는 서버에 보낼 곳이 없었으면(`transport`가 `null`·루트 확보 실패) `null`이다.
   * `stale`은 **보내 본 루트가 서버에 없다고 확인됐는데 갈아탈 새 루트를 못 얻은** 경우다 —
   * 호출자가 들고 있던 id는 죽었으니 어디에도 쓰면 안 된다(`analyId`는 그때도 `null`이다).
   */
  submitFinal(bodies: AnalySubmitBody[]): Promise<FinalResult>;
  /** 아직 서버에 못 올린 테이크. 진단 흐름은 안 보지만 테스트와 디버그가 본다. */
  pending(): AnalyStereo[];
}

const MAX_ATTEMPTS = 3;
const BACKOFF_BASE_MS = 1000;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * 응답 하나를 어떻게 볼 것인가.
 *
 * - `retry` — 5xx이거나 응답을 못 받았다. 서버·회선 문제라 같은 요청이 나중에 성공한다
 * - `drop` — 4xx. 요청 자체가 틀렸으므로 같은 body를 다시 보내도 같은 답이 온다
 * - `closed` — 409. 서버 진단이 이미 `done`이라 **더 들어갈 것이 없다**(C5). 성공으로 보고
 *   남은 큐까지 버린다 — 안 버리면 남은 테이크가 영구 409를 맞는다
 * - `stale` — 404. 루트가 서버에 없다(`findOwnedModel`의 `@id[..] is not found`). body가
 *   틀린 것이 아니라 **id가 틀린 것**이라 같은 4xx라도 `drop`과 처분이 다르다: 루트를 새로
 *   확보하면 같은 body가 그대로 통한다. 이 갈래가 없으면 미러된 옛 id 하나로 그 회차의 네
 *   테이크가 통째로 사라진다(2026-09-16 실측: 네이티브가 localStorage를 AsyncStorage로
 *   미러해 두어, 다른 uid로 만든 `1000034:5`가 복원되고 제출 7건이 전부 404였다)
 */
type Verdict = 'ok' | 'retry' | 'drop' | 'closed' | 'stale';

const verdictOf = (result: TransportResult): Verdict => {
  if (result.ok) {
    return 'ok';
  }
  if (result.status === undefined || result.status >= 500) {
    return 'retry';
  }
  if (result.status === 409) {
    return 'closed';
  }
  return result.status === 404 ? 'stale' : 'drop';
};

export const createAnalySession = (deps: AnalySessionDeps): AnalySession => {
  const { transport } = deps;
  const wait = deps.wait ?? sleep;
  /** 아직 못 올린 테이크. 같은 `stereo`는 마지막 것만 남는다 — 서버도 그렇게 덮어쓴다. */
  const queue = new Map<AnalyStereo, AnalySubmitBody>();
  /** 서버 진단이 `done`으로 닫혔다. 더 보낼 것이 없다. */
  let closed = false;
  /**
   * 루트를 이미 한 번 새로 만들었다. **회차당 한 번뿐이다** — `POST /analy/0`은 `analyNo`를
   * 원자적으로 올리는 비멱등 요청이라(SPEC D28) 404마다 다시 만들면 진단이 계속 늘어난다.
   */
  let renewed = false;

  /**
   * 이번 호출이 쓸 전송기와 루트 id. **공개 호출마다 한 번만 묻는다** — 드레인 도중에 다시
   * 물으면 회차를 마감하며 저장소를 비운 뒤(`clearAnalySessionId`) 나가는 제출이 id를 잃고,
   * 하필 그게 산출물을 실은 마지막 제출이다.
   */
  const open = async (): Promise<{
    transport: AnalyTransport;
    analyId: string;
  } | null> => {
    if (transport === null || closed) {
      return null;
    }
    const analyId = await deps.analyId();
    return analyId === null ? null : { transport, analyId };
  };

  /** 한 테이크를 보낸다. 재시도까지 여기서 끝낸다. */
  const send = async (
    active: AnalyTransport,
    analyId: string,
    body: AnalySubmitBody,
  ): Promise<Verdict> => {
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
      // 전송기가 던지는 것도 "응답을 못 받았다"와 같은 사실이다 — 여기서 흡수하지 않으면
      // 진단 화면이 네트워크 예외로 무너진다.
      const result = await active
        .submit(analyId, body)
        .catch((): TransportResult => ({ ok: false }));
      const verdict = verdictOf(result);
      if (verdict !== 'retry' || attempt === MAX_ATTEMPTS) {
        return verdict;
      }
      await wait(BACKOFF_BASE_MS * 2 ** (attempt - 1));
    }
    return 'retry';
  };

  /**
   * 큐를 비운다. 성공·거절은 큐에서 빠지고, 재시도 대상만 남는다. 끝난 자리의 루트를
   * 돌려준다(404로 갈아탔으면 새 루트). 404인데 갈아타지 못했으면 죽은 루트이므로 id 없이
   * `stale`이다.
   */
  const drain = async (
    active: AnalyTransport,
    analyId: string,
  ): Promise<FinalResult> => {
    let currentId = analyId;
    /** 보내 본 루트가 없다고 확인됐는데 갈아타지 못했다 — 끝난 뒤 `currentId`는 쓸 수 없다. */
    let stale = false;
    for (const [stereo, body] of [...queue]) {
      if (closed) {
        queue.clear();
        return { analyId: currentId, stale: false };
      }
      let verdict = await send(active, currentId, body);
      if (verdict === 'stale') {
        if (renewed || !deps.resetAnalyId) {
          // 이미 한 번 새로 만들었는데 또 404다. 루트 문제가 아니므로 더 시도하지 않는다.
          // 갈아탈 길이 없는 404(`resetAnalyId` 없음)는 그 id가 죽은 것이다. 이미 새로 만든
          // 루트라면(`renewed`) 방금 만든 것이 서 있을 가능성이 높으니 id를 그대로 둔다.
          stale = !renewed;
          verdict = 'drop';
        } else {
          renewed = true;
          deps.resetAnalyId();
          const next = await deps.analyId();
          if (next === null) {
            // 새 루트를 못 얻었다(오프라인·서버 실패). 큐를 남겨 다음 제출이 다시 간다.
            return { analyId: null, stale: true };
          }
          currentId = next;
          verdict = await send(active, currentId, body);
          // 새 루트에도 404면 이 body로는 답이 안 바뀐다. 여기서 버리는 것은 이 모듈의
          // 기존 정책 그대로다 — 화면은 제출 결과를 보지 않는다(파일 헤더). 사용자에게
          // 보이는 신호는 없고, 그것이 의도다: 소비자 온보딩에서 네트워크 사정을 사용자
          // 문제로 만들지 않는다.
          if (verdict === 'stale') {
            verdict = 'drop';
          }
        }
      }
      if (verdict === 'closed') {
        closed = true;
        queue.clear();
        return { analyId: currentId, stale: false };
      }
      if (verdict !== 'retry') {
        queue.delete(stereo);
      }
    }
    return { analyId: stale ? null : currentId, stale };
  };

  return {
    submitTake: async (body) => {
      const run = await open();
      if (run === null) {
        return;
      }
      queue.set(body.stereo, body);
      await drain(run.transport, run.analyId);
    },
    submitFinal: async (bodies) => {
      const run = await open();
      if (run === null) {
        return { analyId: null, stale: false };
      }
      // `Map.set`은 이미 있는 키를 **제자리에** 두므로, 갈아끼우지 않으면 앞서 실패해 큐에
      // 남은 테이크가 계속 첫 자리를 차지해 산출물 제출이 마지막이 아니게 된다.
      queue.clear();
      for (const body of bodies) {
        queue.set(body.stereo, body);
      }
      return drain(run.transport, run.analyId);
    },
    pending: () => [...queue.keys()],
  };
};
